// Step-up (re-authentication) client — auth-service /auth/reauthenticate.
// Sensitive actions (e.g. delegating payroll / user-admin capabilities) need a
// FRESH proof of identity on top of the session. The proof is the ONE factor
// the account requires: a two-factor code when 2FA is on, else the password.
// The backend records a short-lived marker; the gated call reads it.
import axios from 'axios';
import { attachCookieAuth } from '@/lib/session';

const AUTH_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
const api = (() => {
  const i = axios.create({ baseURL: AUTH_BASE, timeout: 30000, withCredentials: true });
  i.interceptors.request.use(attachCookieAuth);
  return i;
})();

export type ReauthMethod = 'totp' | 'password';

export interface ReauthStatus {
  reauthenticated: boolean;
  /** Factor this account must present; null when it has neither a password
   *  nor 2FA (e.g. a Google-only login) and cannot step up. */
  method: ReauthMethod | null;
  expiresAt: string | null;
}

export async function getReauthStatus(): Promise<ReauthStatus> {
  const { data } = await api.get('/api/v1/auth/reauthenticate/status');
  return {
    reauthenticated: data?.reauthenticated === true,
    method: data?.method === 'totp' || data?.method === 'password' ? data.method : null,
    expiresAt: typeof data?.expiresAt === 'string' ? data.expiresAt : null,
  };
}

export async function reauthenticate(input: { password?: string; code?: string }): Promise<{ expiresAt: string }> {
  const { data } = await api.post('/api/v1/auth/reauthenticate', input);
  return { expiresAt: String(data?.expiresAt ?? '') };
}

/** True when a request was refused ONLY because a fresh step-up is missing —
 *  the caller should prompt for it and retry the same request. */
export function isReauthRequiredError(e: unknown): boolean {
  const res = (e as any)?.response;
  return res?.status === 403 && res?.data?.code === 'REAUTH_REQUIRED';
}
